import { HTMLAttributes, RefObject, SyntheticEvent, useCallback, useMemo, useRef } from 'react';
import {
  Autocomplete,
  Button,
  FilterOptionsState,
  IconButton,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { IFormField, IFormFieldSelectMeta } from '../../../view-models/form.view-model';
import { formSelectInlineClassNames } from '../../../theme/component-styles';
import { IBaseFieldProps } from './types';

const actionValue = -1;
interface IActionOption {
  value: number;
  inputValue?: string;
}

export function FieldSelect(props: IBaseFieldProps<IFormFieldSelectMeta<SelectType | string>>) {
  const { formField, formProps } = props;
  const { ref, onChange, onBlur, value } = formProps;
  const inputRef = useRef<HTMLInputElement>(null);
  const { disabled = false, variant, disableClearable, selectOnFocus } = formField ?? {};
  const { inputEndAdornment } = formField.rendererMeta ?? {};
  const className = variant === 'form-inline' ? formSelectInlineClassNames : '';
  const {
    getOptionsLabel,
    options,
    getOptionDisabled,
    groupBy,
    renderOption,
    componentsProps,
    filterOptions,
  } = useFieldSelect(formField, inputRef);

  const _onChange = useCallback(
    (event: SyntheticEvent, newValue: SelectType | null) => {
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <Autocomplete
      className={className}
      componentsProps={componentsProps}
      disableClearable={disableClearable}
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionsLabel}
      groupBy={groupBy}
      isOptionEqualToValue={(option, value) => option === value}
      onBlur={onBlur}
      onChange={_onChange}
      openOnFocus
      options={options}
      selectOnFocus={selectOnFocus}
      renderInput={(params) => (
        <TextField
          {...params}
          ref={ref}
          inputRef={inputRef}
          placeholder={formField.placeholder}
          autoFocus={formField.autoFocus}
          // The `aria-label` above names the Autocomplete's root div, which leaves the
          // combobox itself unnamed. `params.inputProps` carries Autocomplete's own wiring.
          slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': formField?.label } }}
          // Inside the input rather than beside it: an adornment outside takes width from the
          // field, so a column of them leaves every input a different size.
          InputProps={
            inputEndAdornment
              ? {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {inputEndAdornment}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }
              : params.InputProps
          }
        />
      )}
      renderOption={renderOption}
      value={value ?? null}
    />
  );
}

export type SelectType = string | number;

export function useFieldSelect(
  field: IFormField<IFormFieldSelectMeta<SelectType>>,
  inputRef?: RefObject<HTMLInputElement | null>
) {
  const actionableProps = field.rendererMeta?.actionMeta;
  const { actionLabel, helperText, onDelete } = actionableProps ?? {};

  const valuesAsMap = useMemo(() => {
    const values = field.rendererMeta?.values ?? [];
    return values.reduce((res, choice) => {
      return res.set(choice.value, choice);
    }, new Map<SelectType, ISimpleChoice<SelectType>>());
  }, [field.rendererMeta?.values]);

  const getOptionsLabel = useCallback(
    (option: SelectType | string) => {
      const choiceItem = valuesAsMap.get(option) ?? { displayName: '', value: '' };
      return choiceItem.displayName ?? String(choiceItem.value);
    },
    [valuesAsMap]
  );

  const getAllLabels = useCallback(
    (option: SelectType) => {
      const choiceItem = valuesAsMap.get(option) ?? { displayName: '', value: '' };
      return {
        label: choiceItem.displayName ?? String(choiceItem.value),
        secondaryLabel: choiceItem.secondaryLabel,
        caption: choiceItem.caption,
      };
    },
    [valuesAsMap]
  );

  const twoLine = field.rendererMeta?.optionLayout === 'two-line';

  // Two-line options exist because the label alone does not distinguish them, so matching
  // only the label would leave the extra lines unsearchable. Scoped to two-line mode to
  // leave every other select's filtering exactly as it was.
  const _filterOptions = createFilterOptions<SelectType | IActionOption>(
    twoLine
      ? {
          stringify: (option) => {
            if (isActionOption(option)) {
              return '';
            }
            const { label, secondaryLabel, caption } = getAllLabels(option);
            return [label, secondaryLabel, caption, String(option)].filter(Boolean).join(' ');
          },
        }
      : undefined
  );
  const filterOptions = useCallback(
    (
      options: (SelectType | IActionOption)[],
      optionsState: FilterOptionsState<SelectType | IActionOption>
    ) => {
      const filtered = _filterOptions(options, optionsState);
      if (actionableProps?.onAction) {
        const { inputValue } = optionsState;
        if (filtered[0] && isActionOption(filtered[0])) {
          filtered.shift();
        }
        filtered.unshift({
          value: actionValue,
          inputValue: inputValue || undefined,
        });
      }
      return filtered;
    },
    [_filterOptions, actionableProps?.onAction]
  );

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: SelectType | IActionOption) => {
      if (isActionOption(option)) {
        return actionableProps?.onAction ? (
          <Button
            key={option.value}
            role='option'
            component='li'
            fullWidth
            sx={{ justifyContent: 'start', pl: '1.125em', mt: '-0.5rem' }}
            color='secondary'
            size='medium'
            onClick={() => {
              inputRef?.current?.blur();
              actionableProps.onAction!(option.inputValue);
            }}
          >
            {`${actionLabel}${option.inputValue ? ` ${option.inputValue}` : ''}`}
          </Button>
        ) : null;
      } else {
        const { label, secondaryLabel, caption } = getAllLabels(option);
        // `noWrap` only in the two-line layout, where a wrapping sample would push the
        // caption onto a third line. Single-line options keep wrapping, as they always did.
        const adornments = (noWrapSecondary: boolean) => (
          <Stack direction={'row'} alignItems={'center'} gap='0.25rem'>
            {secondaryLabel && (
              <Typography variant='caption' color='text.secondary' noWrap={noWrapSecondary}>
                {' '}
                {secondaryLabel}
              </Typography>
            )}
            {onDelete && (
              <IconButton
                aria-label='delete'
                color={'secondary'}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(option);
                }}
              >
                <DeleteOutlineIcon fontSize={'small'} />
              </IconButton>
            )}
          </Stack>
        );

        if (twoLine) {
          return (
            <Stack component='li' {...props} key={option as SelectType} direction={'column'}>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                gap='1rem'
                width={'100%'}
              >
                <Typography variant='body2' noWrap title={label}>
                  {label}
                </Typography>
                {adornments(true)}
              </Stack>
              {caption && (
                <Typography variant='caption' color='text.secondary' noWrap width={'100%'} title={caption}>
                  {caption}
                </Typography>
              )}
            </Stack>
          );
        }

        return (
          <Stack
            component='li'
            {...props}
            key={option as SelectType}
            direction={'row'}
            justifyContent={'space-between !important'}
          >
            <Typography variant='body2' noWrap title={label}>
              {label}
            </Typography>
            {adornments(false)}
          </Stack>
        );
      }
    },
    [actionLabel, actionableProps, getAllLabels, onDelete, inputRef, twoLine]
  );

  const componentsProps = useMemo(() => {
    if (!actionableProps) {
      return undefined;
    }
    return { paper: getSelectPaperProps(helperText) };
  }, [actionableProps, helperText]);

  const groupBy = useMemo(() => {
    if (!field.rendererMeta?.enableGrouping) {
      return undefined;
    }
    return (option: SelectType) => {
      return valuesAsMap.get(option)?.group ?? '';
    };
  }, [field.rendererMeta?.enableGrouping, valuesAsMap]);

  return {
    filterOptions:
      actionableProps?.onAction || twoLine
        ? (filterOptions as (opts: SelectType[], optState: FilterOptionsState<SelectType>) => SelectType[])
        : undefined,
    getOptionsLabel,
    groupBy,
    options: Array.from(valuesAsMap.keys()),
    valuesAsMap,
    getOptionDisabled: (option: SelectType) => valuesAsMap.get(option)?.disabled ?? false,
    actionableProps,
    renderOption,
    componentsProps,
  };
}

export function getSelectPaperProps(helperText?: string) {
  return {
    sx: {
      minWidth: 'max-content',
      paddingTop: helperText ? '0.5rem' : '0.2rem',
      ':before': helperText
        ? {
            content: `'${helperText}'`,
            transform: { translate: '0, 1rem' },
            marginLeft: '1rem',
            fontSize: '0.75rem',
            color: 'text.secondary',
          }
        : undefined,
    },
  };
}

function isActionOption(value: SelectType | IActionOption): value is IActionOption {
  return (value as IActionOption).value === actionValue;
}
